/* eslint-disable camelcase */
import { Cloudinary } from 'cloudinary-core';
import { gqlMedia, gqlMediaStorageStatusEnum } from '@minecraft.graphql-types';
import { Media } from '@minecraft.graphql-operations';
import { getEnvironment } from '@minecraft.environment';
import { DeepPartial } from '@minecraft.utils';

const cloudinaryEnvironment = () => ({
  hotStorageCloudName: getEnvironment().CN_HOT_CLOUDNAME,
  coldStorageCloudName: getEnvironment().CN_COLD_CLOUDNAME,
  domain: getEnvironment().CLD_DOMAIN,
});

export const isMediaInColdStorage = (mediaStorageStatusCode: string | gqlMediaStorageStatusEnum): boolean =>
  [gqlMediaStorageStatusEnum.BAU_MIGRATED, gqlMediaStorageStatusEnum.CLD_CLEANED].includes(
    mediaStorageStatusCode as gqlMediaStorageStatusEnum
  );

/**
 * shared helper to build the cloudinary core instance that is used to
 * generate the audio / video / photo urls for the legacy cloudinary prop shims
 */
export const getCloudinary = (mediaStorageStatus: { code: string }): Cloudinary => {
  const env = cloudinaryEnvironment();
  /** determine if we use the hot or cold cloud in cloudinary for this media based on its storage status */
  const cloudName = isMediaInColdStorage(mediaStorageStatus.code) ? env.coldStorageCloudName : env.hotStorageCloudName;

  /** return an instance of the Cloudinary core library with the correct domain & cloud name */
  return new Cloudinary({
    cloud_name: cloudName,
    secure: true,
    secure_distribution: env.domain,
  });
};

const removeFileExtension = (fileKey: string): string => {
  // split the fileKey by dot to remove the extension
  const parts = fileKey.split('.');

  // if there was an extension, remove it
  const possibleExtension = parts[parts.length - 1];
  if (
    parts?.length > 1 &&
    ['jpg', 'jpe', 'jpeg', 'png', 'gif', 'aiff', 'm4a', 'mp3', 'ogg', 'wav', 'mov', 'mkv', 'mp4', 'pdf'].includes(
      possibleExtension.toLowerCase()
    )
  ) {
    parts.pop();
  }

  // put any other parts back together but the period replaced with dash -
  return parts.join('.');
};

const NON_SAFE_CHARS = /[^0-9A-Za-z. ]/gi;
const SPACES = / /g;
const PERIODS = /\./g;

// as per cloudinary docs - all dots should be escaped by %252E, and symbols aren't allowed
// https://cloudinary.com/documentation/transformation_flags
export const sanitizeDownloadFileName = (fileName: string): string => {
  if (typeof fileName !== 'string' || !fileName.length) {
    // generic fallback name
    return 'download';
  }

  return removeFileExtension(fileName)
    .replace(NON_SAFE_CHARS, '')
    .trim()
    .replace(SPACES, '_')
    .replace(PERIODS, '%252E');
};

export type GetMediaDownloadOriginalUrlArgs = {
  primaryUrl: string;
};

/**
 * Sets the download URL to use the "media.castingnetworks.com" domain instead of the bunnyCDN (bmedia)
 */
export const getMediaDownloadOriginalUrl = ({ primaryUrl }: GetMediaDownloadOriginalUrlArgs): string => {
  if (!primaryUrl) {
    return '';
  }

  const environment = getEnvironment();
  const cldDomain = environment.CLD_DOMAIN;
  const cldMediaDomain = environment.CLD_MEDIA_DOMAIN;
  // the bmedia domain is a CDN that is used to serve media files and it can get a cached version
  // of the URL without the correct headers that tell the browser to download the file instead of trying to play it in a tab
  // we use the base CDN domain without caching here to prevent this error
  // original bug: https://castingnetworks.atlassian.net/browse/CNEE-2500
  return primaryUrl.replace(cldDomain, cldMediaDomain);
};

export const getMediaNameWithExtension = <T extends DeepPartial<gqlMedia> | DeepPartial<Media>>({
  media,
}: {
  media: T;
}): string => {
  if (!media?.name) return '';

  let mediaNameBase = media?.name;
  const extension = media?.fileType?.code?.toLowerCase();

  if (media?.fileType?.code && !mediaNameBase?.endsWith(extension)) {
    mediaNameBase = `${mediaNameBase}.${extension}`;
  }

  return mediaNameBase;
};

export const isMediaLocked = <T extends DeepPartial<gqlMedia> | DeepPartial<Media>>({ media }: { media: T }) => {
  return media?.isLocked || media?.mediaStatus?.code === 'ARCHIVED';
};
